// Privacy Policy content + version constant.
//
// Drafted as senior counsel under the Digital Personal Data Protection
// Act, 2023 (DPDP Act) — India's privacy law that came into force in
// 2023 and is the primary statute we must comply with.
//
// Key DPDP obligations covered:
//   - Notice (Section 5): purpose, categories of data, contact for grievances
//   - Consent (Section 6): explicit, informed, unambiguous, specific
//   - Data Principal rights (Sections 11-14): access, correction, erasure,
//     grievance redressal, nominate
//   - Data Fiduciary obligations (Sections 8-10): security, breach
//     notification, retention limits, accuracy, processor contracts
//   - Children's data (Section 9): no profiling/tracking under 18
//
// Versioning: same scheme as terms.js. Bump PRIVACY_VERSION on material
// changes. We re-use `terms_acceptances` table — separating the two
// would force users to accept the same row twice. The UI bundles them
// in a single "I agree" checkbox.

export const PRIVACY_VERSION = "1.0";
export const PRIVACY_EFFECTIVE_DATE = "2026-04-26";

export const COMPANY_NAME = "Rajieev House Rental";
export const COMPANY_JURISDICTION = "Sivakasi, Tamil Nadu, India";

// Replace before public launch — DPDP Section 5 requires a real
// reachable contact for the Data Protection Officer / grievance officer.
export const GRIEVANCE_OFFICER = {
    name: "Privacy Officer",
    email: "privacy@rajieev-house-rental.example",   // TODO: replace with real
    address: COMPANY_JURISDICTION,
    responseSLA: "30 days",
};

// Sections rendered top-to-bottom (no accordion — DPDP transparency
// expectation is full-disclosure, not progressive).
export const PRIVACY_SECTIONS = [
    {
        id: "intro",
        title: "1. Introduction",
        body: [
            `${COMPANY_NAME} ("we", "us", "our") respects your privacy. This Privacy Policy explains what personal data we collect about you, how we use it, with whom we share it, how long we keep it, and your rights under the Digital Personal Data Protection Act, 2023 ("DPDP Act").`,
            `This Policy applies to your use of the ${COMPANY_NAME} mobile application, web application, and related services (collectively, the "Service"), regardless of how you access them.`,
            `By creating an account or using the Service, you confirm you have read and understood this Policy. Your continued use of the Service is your ongoing consent to processing as described here. You can withdraw consent at any time (see Section 9).`,
        ],
    },
    {
        id: "controller",
        title: "2. Who is the Data Fiduciary?",
        body: [
            `Under the DPDP Act, ${COMPANY_NAME} (registered in ${COMPANY_JURISDICTION}) is the "Data Fiduciary" — the entity that determines the purpose and means of processing your personal data.`,
            `For grievances or to exercise your rights under this Policy, contact our Privacy Officer at ${GRIEVANCE_OFFICER.email}. We will respond within ${GRIEVANCE_OFFICER.responseSLA} as required by Section 13 of the DPDP Act.`,
        ],
    },
    {
        id: "data-collected",
        title: "3. What Personal Data We Collect",
        body: [
            `We collect only the data we need to provide the Service. The categories below are the maximum we may hold about you — actual data depends on your role and how you use the Service.`,
            `**Identity & contact data:** name, phone number, email (if provided), profile photo (if uploaded), Aadhaar last-4 digits only (we never store the full 12 digits).`,
            `**Tenancy data:** property address, rent amount, deposit amount, rent due day, lease start/end dates, parent/guardian name and relation (for rental agreements), permanent address, ID number (last 4 digits only).`,
            `**Payment records:** rent payment amount, date, payment method (UPI / Cash / Bank Transfer / Cheque), pending/verified status. We do NOT store UPI PINs, card numbers, CVVs, bank login credentials, or any payment instrument details.`,
            `**Communication data:** in-app chat messages between landlord and tenant, maintenance request descriptions, photos uploaded to maintenance requests.`,
            `**Device & technical data:** IP address, browser type, device type, push notification subscription endpoint, app version, and timestamps of activity. Captured for security monitoring and fraud prevention.`,
            `**Location data:** GPS coordinates of properties when you choose to share them; stored only with your explicit map-pin action.`,
            `**Subscription data (landlords only):** plan type, payment status, billing transaction IDs (issued by Razorpay — not actual card details).`,
        ],
    },
    {
        id: "purposes",
        title: "4. Why We Process Your Data",
        body: [
            `We use your personal data only for the following purposes (DPDP Act Section 5(1)(i)):`,
            `**(a) Providing the Service:** managing your account, displaying your tenancy/property data, recording payments, sending notifications, enabling chat, generating rental agreements.`,
            `**(b) Security & fraud prevention:** detecting unauthorized access, blocking abuse, enforcing rate limits, investigating suspicious activity.`,
            `**(c) Communications:** transactional notifications about rent, leases, maintenance, plan expiry. You can opt out of marketing communications at any time, but transactional notifications are essential to the Service.`,
            `**(d) Legal compliance:** retaining records for tax (Income Tax Act), tenancy disputes (state Rent Acts), and audit requirements.`,
            `**(e) Service improvement:** aggregated, non-identifying analytics to fix bugs and improve features. We do NOT use individual user data for advertising or sell it to advertisers.`,
            `We do NOT use your data to train AI models or for any purpose not listed here without your fresh, specific consent.`,
        ],
    },
    {
        id: "consent",
        title: "5. Lawful Basis & Consent",
        body: [
            `Our primary lawful basis for processing is your consent (DPDP Act Section 6), given when you accept this Policy at signup.`,
            `Some processing happens under Section 7 ("Certain Legitimate Uses") — for example, retaining records to comply with tax law or to defend against a legal claim. These do not require separate consent.`,
            `We never use "deemed consent" loopholes, dark patterns, or pre-ticked boxes. Consent is always an active opt-in.`,
        ],
    },
    {
        id: "sharing",
        title: "6. Who We Share Data With",
        body: [
            `We share your data only with the parties below, only for the purposes stated:`,
            `**(a) Other users in your tenancy:** landlords see their tenants' data (necessary for tenancy management); tenants see their landlord's payment details (UPI ID, bank info) for rent payment. Service-persons see job-relevant tenant info.`,
            `**(b) Cloud infrastructure providers (Data Processors):** Render (hosting), Supabase (PostgreSQL database) — both bound by data processing contracts requiring DPDP-equivalent protection.`,
            `**(c) Razorpay:** for landlord subscription billing only. Razorpay is a separate Data Fiduciary subject to its own privacy policy. We never share rent payment data with Razorpay (rent is paid directly between landlord and tenant).`,
            `**(d) Communication providers:** WhatsApp Business API (for notifications you opted in to), web-push services (FCM / Mozilla / Apple).`,
            `**(e) Government / law enforcement:** only when required by a valid legal order under Indian law. We will notify you unless legally prohibited.`,
            `We do NOT sell, rent, or trade your personal data to third parties for marketing or any other purpose.`,
        ],
    },
    {
        id: "cross-border",
        title: "7. International Transfers",
        body: [
            `Some of our infrastructure providers (Render, Supabase) may host data on servers located outside India. As required by Section 16 of the DPDP Act, we only transfer data to jurisdictions that provide adequate protection or under contractual safeguards.`,
            `We do not transfer data to any country restricted under DPDP rules. We will update this Policy if the central government issues a list of restricted countries and any of our providers fall on it.`,
        ],
    },
    {
        id: "security",
        title: "8. Security Measures",
        body: [
            `We implement industry-standard technical and organizational measures appropriate to the sensitivity of the data, including:`,
            `**Encryption:** all data in transit is encrypted via HTTPS / TLS 1.2+. Database connections use SSL.`,
            `**Authentication:** passwords are stored as bcrypt hashes (never plaintext); JWT-based sessions; rate-limited login.`,
            `**Access control:** Row-Level Security (RLS) enabled on all database tables; multi-tenant org isolation enforced at the database level.`,
            `**Audit logging:** all login attempts, password changes, and consent events are logged.`,
            `**Breach notification:** in the event of a personal data breach, we will notify the Data Protection Board and affected users without undue delay, as required by DPDP Section 8(6).`,
        ],
    },
    {
        id: "rights",
        title: "9. Your Rights as a Data Principal",
        body: [
            `Under the DPDP Act you have the following rights, exercisable by contacting our Privacy Officer:`,
            `**Right to information (Section 11):** confirm what data we hold about you and how we process it.`,
            `**Right to correction & erasure (Section 12):** correct inaccurate data; delete data we no longer need (subject to legal retention obligations in Section 11).`,
            `**Right of grievance redressal (Section 13):** raise a complaint with our Privacy Officer; we respond within ${GRIEVANCE_OFFICER.responseSLA}. If unresolved, escalate to the Data Protection Board of India.`,
            `**Right to nominate (Section 14):** nominate another individual to exercise your rights in the event of your death or incapacity.`,
            `**Right to withdraw consent:** stop processing any time. We will stop unless we have a separate lawful basis (e.g., legal retention).`,
            `Most rights can be exercised directly in the app: edit your profile, delete attachments, close your account from Settings. For anything not self-serve, email us.`,
        ],
    },
    {
        id: "retention",
        title: "10. How Long We Keep Your Data",
        body: [
            `**Active accounts:** we keep your data for as long as your account is active.`,
            `**Closed accounts:** after closure, we retain personal data for up to 7 years to comply with the Income Tax Act (records retention), the Indian Contract Act (limitation period for disputes), and applicable state Rent Acts. After this period, data is permanently deleted.`,
            `**Audit logs:** terms-acceptance records and security logs are retained for 7 years for legal evidentiary purposes.`,
            `**Backups:** routine backups may persist for up to 90 days after deletion before being overwritten.`,
            `**Anonymized analytics:** aggregated, non-identifying statistics may be retained indefinitely.`,
        ],
    },
    {
        id: "children",
        title: "11. Children's Privacy",
        body: [
            `The Service is not directed at children under 18. Under Section 9 of the DPDP Act, we do NOT undertake tracking, behavioural monitoring, or targeted advertising directed at children.`,
            `If we learn that a person under 18 has created an account without verifiable parental consent, we will delete the account and associated data.`,
            `If you believe a minor is using the Service without consent, please email our Privacy Officer immediately.`,
        ],
    },
    {
        id: "cookies",
        title: "12. Cookies & Local Storage",
        body: [
            `We use browser local storage (NOT third-party cookies) to remember your login state, preferred language, and dark-mode setting. This data stays on your device and is not transmitted to advertisers.`,
            `We do not use Google Analytics, Facebook Pixel, or any third-party analytics that profile users across sites.`,
        ],
    },
    {
        id: "changes",
        title: "13. Changes to This Policy",
        body: [
            `We may update this Policy from time to time. Material changes (new categories of data collected, new sharing recipients, change in retention period) will be notified via in-app notice and you will be required to re-accept the updated Policy.`,
            `The current version is shown at the top of this document. The effective date marks when this version takes effect.`,
        ],
    },
    {
        id: "contact",
        title: "14. Contact & Grievance Redressal",
        body: [
            `**Privacy Officer / Grievance Officer:**`,
            `Name: ${GRIEVANCE_OFFICER.name}`,
            `Email: ${GRIEVANCE_OFFICER.email}`,
            `Address: ${GRIEVANCE_OFFICER.address}`,
            `Response SLA: ${GRIEVANCE_OFFICER.responseSLA} (as required by DPDP Section 13(3))`,
            `If your grievance is not satisfactorily resolved by us, you have the right to escalate to the Data Protection Board of India under DPDP Section 28.`,
        ],
    },
];

// Convenience: full plain-text rendering for legal record / export.
export function renderPrivacyAsText() {
    const header = `${COMPANY_NAME} — Privacy Policy\nVersion ${PRIVACY_VERSION} • Effective ${PRIVACY_EFFECTIVE_DATE}\n\n`;
    return header + PRIVACY_SECTIONS.map(s => `${s.title}\n\n${s.body.join("\n\n")}\n\n`).join("");
}
